// Profit bars (green, red for a loss) on the left axis + orders line on the
// right. Recharts names are aliased with an R prefix so they can never clash
// with lucide-react icons (Line, BarChart, Tooltip-ish names) in this tree.
import {
  ResponsiveContainer,
  ComposedChart,
  CartesianGrid as RGrid,
  XAxis as RXAxis,
  YAxis as RYAxis,
  Tooltip as RTooltip,
  Bar as RBar,
  Line as RLine,
  Cell as RCell,
  ReferenceLine as RReferenceLine,
} from 'recharts'
import { money, num, isNum, dayLabel, dowOf } from './format'

const GREEN = '#10b981'
const RED = '#dc2626'
const BLUE = '#2563eb'
const DOW1 = ['S', 'M', 'T', 'W', 'T', 'F', 'S']

function DayTick({ x, y, payload }) {
  const iso = payload?.value
  if (!iso) return null
  const d = Number(String(iso).slice(8, 10))
  return (
    <g transform={`translate(${x},${y})`}>
      <text dy={11} textAnchor="middle" fontSize={10} fill="#64748b">{d}</text>
      <text dy={22} textAnchor="middle" fontSize={9} fill="#94a3b8">{DOW1[dowOf(iso)]}</text>
    </g>
  )
}

function TipBody({ active, payload }) {
  if (!active || !payload?.length) return null
  const p = payload[0]?.payload
  if (!p) return null
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm px-3 py-2 text-xs">
      <div className="font-semibold text-slate-800 mb-1">{dayLabel(p.date)}</div>
      <div>Profit: <span className={isNum(p.profit) && p.profit < 0 ? 'text-red-600' : ''}>{money(p.profit)}</span></div>
      <div>Orders: {num(p.orders)}</div>
      <div>Labour hours: {num(p.labour_hours, 1)}</div>
      <div>Break-even orders: {num(p.breakeven_orders)}</div>
    </div>
  )
}

/** days: rows from GET /daily. targetPerDay: optional reference line (null = none). */
export default function DailyChart({ days, targetPerDay }) {
  // Recharts draws null as a gap, which is what an unknown profit should be.
  const data = days.map((d) => ({ ...d, profit: isNum(d.profit) ? Number(d.profit) : null }))
  return (
    <ResponsiveContainer width="100%" height={260}>
      <ComposedChart data={data} margin={{ top: 10, right: 8, bottom: 8, left: 8 }}>
        <RGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
        <RXAxis dataKey="date" tick={<DayTick />} interval={0} height={30} tickLine={false} />
        <RYAxis
          yAxisId="profit"
          tick={{ fontSize: 11, fill: '#64748b' }}
          tickFormatter={(v) => money(v)}
          width={64}
        />
        <RYAxis
          yAxisId="orders"
          orientation="right"
          tick={{ fontSize: 11, fill: BLUE }}
          allowDecimals={false}
          width={40}
        />
        <RTooltip content={<TipBody />} />
        <RReferenceLine yAxisId="profit" y={0} stroke="#94a3b8" />
        {isNum(targetPerDay) && (
          <RReferenceLine
            yAxisId="profit"
            y={Number(targetPerDay)}
            stroke="#059669"
            strokeDasharray="4 4"
            label={{ value: 'target', position: 'insideTopLeft', fontSize: 10, fill: '#059669' }}
          />
        )}
        <RBar yAxisId="profit" dataKey="profit" radius={[2, 2, 0, 0]} maxBarSize={28} isAnimationActive={false}>
          {data.map((d) => <RCell key={d.date} fill={isNum(d.profit) && d.profit < 0 ? RED : GREEN} />)}
        </RBar>
        <RLine
          yAxisId="orders"
          type="linear"
          dataKey="orders"
          stroke={BLUE}
          strokeWidth={1.8}
          dot={{ r: 2.6, fill: BLUE, strokeWidth: 0 }}
          isAnimationActive={false}
        />
      </ComposedChart>
    </ResponsiveContainer>
  )
}
